import classNames from 'classnames';
import { defineMessages, useIntl } from 'react-intl';
import Image from '@alltrails/core/components/Image';
import ShimmerContainer from '@alltrails/core/components/ShimmerContainer';
import type useTrailPhotos from '@alltrails/trails/hooks/useTrailPhotos';
import getTrailLocationName from '@alltrails/trails/utils/getTrailLocationName';
import type TrailDetails from '@alltrails/types/TrailDetails';
import type TrailPhoto from '@alltrails/types/TrailPhoto';
import HeroImage from '../HeroImage/HeroImage';
import layoutStyles from '../layouts/layoutStyles.module.scss';
import styles from './DesktopHeroGrid.module.scss';
import StaticMapButton from './StaticMapButton';

const messages = defineMessages({
  HERO_BUTTON: { defaultMessage: 'Show trail photo {index} of {photoCount, number}' }
});

type TrailPhotos = ReturnType<typeof useTrailPhotos>;

type HeroGridContentsProps = {
  defaultPhotoAlt: string;
  defaultPhotoUrl: string;
  isBot: boolean;
  lightboxPhotos: TrailPhotos['lightboxPhotos'];
  loadState: TrailPhotos['loadState'];
  mapId: number;
  onMapFallbackClick: (isExpandButtonClicked: boolean) => void;
  onPhotoClick: (index: number) => void;
  photoCount: number;
  trailDetails: TrailDetails;
  trailName: string;
  defaultPhoto?: TrailPhoto;
};

const HeroGridContents = ({
  defaultPhotoAlt,
  defaultPhotoUrl,
  isBot,
  lightboxPhotos,
  loadState,
  mapId,
  onMapFallbackClick,
  onPhotoClick,
  photoCount,
  trailDetails,
  trailName,
  defaultPhoto
}: HeroGridContentsProps) => {
  const intl = useIntl();
  const mapImageAlt = intl.formatMessage({ defaultMessage: `{name} map` }, { name: trailName });

  const getSecondPhotoAltText = () => {
    if (trailDetails.area?.name) {
      return intl.formatMessage({ defaultMessage: 'Second photo of {name} in {park}' }, { name: trailName, park: trailDetails.area.name });
    } else if (trailDetails.location?.city) {
      return intl.formatMessage(
        { defaultMessage: 'Second photo of {name} near {cityRegionAndCountry}' },
        { name: trailName, cityRegionAndCountry: getTrailLocationName(trailDetails) }
      );
    } else if (trailDetails.location?.regionName) {
      return intl.formatMessage(
        { defaultMessage: 'Second photo of {name} in {region}' },
        { name: trailName, region: trailDetails.location.regionName }
      );
    }

    return intl.formatMessage({ defaultMessage: 'Second photo of {name}' }, { name: trailName });
  };

  if ((loadState !== 'loaded' && photoCount > 2) || (loadState === 'loaded' && lightboxPhotos.length > 2)) {
    return (
      <>
        <button
          aria-label={intl.formatMessage(messages.HERO_BUTTON, { index: 1, photoCount })}
          className={classNames(layoutStyles.col4, styles.imageContainer, styles.primaryImage, styles.scrim)}
          onClick={() => onPhotoClick(0)}
        >
          <Image
            alt={defaultPhotoAlt}
            className={styles.image}
            fill
            isLcp
            /**
             * Responsive image sizing calculation for 4-column width in 6-column grid
             *
             * Breakpoint: 1216px = $breakpoint-md-size + 2 * $breakpoint-md-margin
             *
             * Below 1216px (dynamic viewport-based):
             *   Full: 4 * (((100vw - (2 * var(--breakpoint-md-margin))) - (5 * var(--space-100))) / 6) + (3 * var(--space-100))
             *   Simplified: calc(200vw / 3 - 25rem / 6)
             *
             * Above 1216px (fixed container width):
             *   Full: 4 * ((var(--breakpoint-md-size) - (5 * var(--space-100))) / 6) + (3 * var(--space-100))
             *   Simplified: 744px
             *
             * Note: Using separate conditions instead of min/max due to Safari compatibility issues
             */
            sizes="(max-width: 1216px) calc(200vw / 3 - 25rem / 6), 744px"
            src={defaultPhotoUrl}
            title={defaultPhotoAlt}
          />
        </button>
        <div className={classNames(layoutStyles.col2, styles.verticalImages)}>
          {lightboxPhotos.length > 1 ? (
            <button
              aria-label={intl.formatMessage(messages.HERO_BUTTON, { index: 2, photoCount })}
              className={classNames(styles.imageContainer, styles.secondaryImage)}
              onClick={() => onPhotoClick(1)}
            >
              <Image
                alt={getSecondPhotoAltText()}
                className={styles.image}
                handleLoadingState
                fill
                /**
                 * Responsive image sizing calculation for 2-column width in 6-column grid
                 *
                 * Breakpoint: 1216px = $breakpoint-md-size + 2 * $breakpoint-md-margin
                 *
                 * Below 1216px (dynamic viewport-based):
                 *   Full: 2 * (((100vw - (2 * var(--breakpoint-md-margin))) - (5 * var(--space-100))) / 6) + (3 * var(--space-100))
                 *   Simplified: calc((200vw - 17rem) / 6 + 1.5rem)
                 *
                 * Above 1216px (fixed container width):
                 *   Full: 2 * ((var(--breakpoint-md-size) - (5 * var(--space-100))) / 6) + (3 * var(--space-100))
                 *   Simplified: 372px
                 *
                 * Note: Using separate conditions instead of min/max due to Safari compatibility issues
                 */
                sizes="(max-width: 1216px) calc((200vw - 17rem) / 6 + 1.5rem), 372px"
                src={lightboxPhotos[1].imgUrl}
              />
            </button>
          ) : (
            <ShimmerContainer className={classNames(styles.imageContainer, styles.secondaryImage, styles.placeholderImage)}>{null}</ShimmerContainer>
          )}
          {!isBot && (
            <StaticMapButton
              className={classNames(styles.imageContainer, styles.secondaryImage)}
              imageAlt={mapImageAlt}
              mapId={mapId}
              onClick={onMapFallbackClick}
              variant="inset"
            />
          )}
        </div>
      </>
    );
  }

  if (defaultPhoto) {
    return (
      <>
        <button
          aria-label={intl.formatMessage(messages.HERO_BUTTON, { index: 1, photoCount })}
          className={classNames(layoutStyles.col4, styles.imageContainer, styles.primaryImage, styles.scrim)}
          onClick={() => onPhotoClick(0)}
        >
          <HeroImage alt={defaultPhotoAlt} isLcp photoUrl={defaultPhotoUrl} />
        </button>
        {!isBot && (
          <StaticMapButton
            className={classNames(layoutStyles.col2, styles.imageContainer, styles.secondaryImage)}
            imageAlt={mapImageAlt}
            mapId={mapId}
            onClick={onMapFallbackClick}
            variant="tall"
          />
        )}
      </>
    );
  }

  if (isBot) {
    return null;
  }

  return (
    <button
      aria-label={intl.formatMessage({ defaultMessage: 'Expand map' })}
      className={classNames(layoutStyles.col6, styles.imageContainer, styles.singleImage)}
      onClick={() => onMapFallbackClick(false)}
    >
      <HeroImage alt={defaultPhotoAlt} isLcp photoUrl={defaultPhotoUrl} />
    </button>
  );
};

export default HeroGridContents;
